/*
  店舗在庫確認モーダル（SQ連携）
  - トリガ: [data-store-stock]（c-main-product の「店舗在庫を確認」ボタン）
  - 在庫取得: SQ SDK getLocationAvailabilities(sku, first, cursor) をページネーションで全件取得
  - バリアント選択 → SKU 解決 → 再取得（取得中にSKUが変わったらstaleとして破棄）
  参考実装: Tabio c_sq-store-inventory
*/
document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-store-inventory-root]');
  if (!root) return;

  const configEl = root.querySelector('[data-store-inventory-config]');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.warn('[c-store-inventory] config parse failed', e);
    return;
  }

  const dialog = root.querySelector('[data-store-inventory-dialog]');
  const noticeEl = root.querySelector('.c-store-inventory__notice');
  const listEl = root.querySelector('[data-store-inventory-list]');
  const moreBtn = root.querySelector('[data-store-inventory-more]');
  const errorEl = root.querySelector('[data-store-inventory-error]');
  const selects = root.querySelectorAll('.c-store-inventory__select');
  const triggerBtn = document.querySelector('[data-store-stock]');
  if (!dialog || !listEl || !triggerBtn) return;

  const THRESHOLD = Number(config.threshold) || 0;
  const INITIAL_COUNT = Number(config.initialCount) || 2;

  const state = {
    /* 初期選択は selected_or_first_available_variant のオプション（＝実在バリアント）を優先。
       欠番運用で先頭値の組み合わせが実在しない場合に、SQ照会前の「在庫情報なし」誤表示を防ぐ。
       config になければ従来どおり各オプションの先頭値へフォールバック。 */
    selectedOptions: (config.initialOptions && config.initialOptions.length)
      ? config.initialOptions.slice()
      : (config.options || []).map((opt) => (opt.values && opt.values[0]) || ''),
    currentSku: null,
    stores: [],
    expanded: false,
    sdk: null,
  };

  /* 選択中オプション → variant → SKU */
  function resolveSku() {
    const variant = (config.variants || []).find((v) =>
      v.options.every((val, i) => val === state.selectedOptions[i])
    );
    return variant && variant.sku ? variant.sku : null;
  }

  function getSdk() {
    if (state.sdk) return state.sdk;
    if (window.SQ && window.SQ.SDK) {
      state.sdk = new window.SQ.SDK(config.origin);
    }
    return state.sdk;
  }

  /* SKU の全ロケーション在庫を取得（hasNextPage を辿る。取得中の SKU 変更は stale として null を返す） */
  async function fetchAllAvailabilities(sdk, sku) {
    const all = [];
    let cursor;
    let guard = 0;
    do {
      const res = await sdk.getLocationAvailabilities(sku, 1000, cursor);
      if (sku !== state.currentSku) return null;
      const nodes = (res && res.nodes) || res || [];
      if (Array.isArray(nodes)) all.push(...nodes);
      cursor = res && res.pageInfo && res.pageInfo.hasNextPage ? res.pageInfo.endCursor : undefined;
      guard += 1;
    } while (cursor && guard < 10);
    return all;
  }

  async function loadStores() {
    const sku = resolveSku();
    state.currentSku = sku;
    state.expanded = false;
    hideError();
    showLoading();
    updateNoticeDate();

    const sdk = getSdk();
    if (!sdk) {
      renderEmpty('在庫情報を取得できませんでした。時間をおいて再度お試しください。');
      console.error('[c-store-inventory] SQ SDK not loaded');
      return;
    }
    if (!sku) {
      renderEmpty('この商品の店舗在庫情報はありません。');
      return;
    }

    try {
      const nodes = await fetchAllAvailabilities(sdk, sku);
      if (nodes === null) return; /* stale */

      state.stores = [];
      /* 実店舗として表示しないSQロケーション（ECフルフィルメント倉庫など） */
      const EXCLUDED_LOCATION_IDS = new Set([
        '17cbdb96-4425-5819-9f28-48efa24ef784_Location', // psycho bunny EC倉庫
        '4d5da036-a2ec-5b32-bc82-419c8a358a95_Location'  // 倉庫（取寄せ用PB）
      ]);

      for (const node of nodes) {
        if (!node || !node.location) continue;
        const loc = node.location;
        if (loc.isClosed) continue;
        if (EXCLUDED_LOCATION_IDS.has(loc.id)) continue;
        const address = loc.address || {};
        state.stores.push({
          name: loc.name || '',
          province: address.province || '',
          address1: address.address1 || '',
          phone: loc.phone || '',
          mapURL: safeURL(loc.mapURL),
          quantity: Number(node.inventoryQuantity) || 0,
        });
      }
      renderStores();
    } catch (e) {
      console.error('[c-store-inventory] fetch failed', e);
      renderEmpty('在庫情報を取得できませんでした。時間をおいて再度お試しください。');
    }
  }

  /* ============ 描画 ============ */

  function showLoading() {
    moreBtn.hidden = true;
    listEl.innerHTML = '<p class="c-store-inventory__loading">読み込み中…</p>';
  }

  function renderEmpty(message) {
    moreBtn.hidden = true;
    listEl.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'c-store-inventory__empty';
    p.textContent = message;
    listEl.appendChild(p);
  }

  function stockState(qty) {
    if (qty > 0 && THRESHOLD > 0 && qty <= THRESHOLD) return 'low';
    if (qty > 0) return 'in';
    return 'out';
  }

  function buildStoreItem(store) {
    const item = document.createElement('div');
    item.className = 'c-store-inventory__item';

    const left = document.createElement('div');
    left.className = 'c-store-inventory__item-left';

    const area = document.createElement('p');
    area.className = 'c-store-inventory__item-area';
    area.textContent = [store.province, store.address1].filter(Boolean).join(' ');
    left.appendChild(area);

    if (store.mapURL) {
      const link = document.createElement('a');
      link.className = 'c-store-inventory__item-name';
      link.href = store.mapURL;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = store.name;
      left.appendChild(link);
    } else {
      const name = document.createElement('p');
      name.className = 'c-store-inventory__item-name';
      name.textContent = store.name;
      left.appendChild(name);
    }

    if (store.phone) {
      const phone = document.createElement('p');
      phone.className = 'c-store-inventory__item-phone';
      phone.textContent = '電話番号：' + store.phone;
      left.appendChild(phone);
    }

    const right = document.createElement('p');
    right.className = 'c-store-inventory__item-stock';
    const s = stockState(store.quantity);
    right.innerHTML =
      '店舗在庫 <span class="c-store-inventory__mark c-store-inventory__mark--' + s + '" aria-hidden="true"></span>' +
      '<span class="visually-hidden">' + (s === 'in' ? '在庫あり' : s === 'low' ? '残りわずか' : '在庫なし') + '</span>';

    item.appendChild(left);
    item.appendChild(right);
    return item;
  }

  function renderStores() {
    listEl.innerHTML = '';
    if (!state.stores.length) {
      renderEmpty('店舗情報が見つかりませんでした。');
      return;
    }
    /* 初期表示分は直置き、超過分は折りたたみコンテナへ（grid 0fr⇄1fr で開閉アニメーション） */
    state.stores.slice(0, INITIAL_COUNT).forEach((store) => listEl.appendChild(buildStoreItem(store)));

    const rest = state.stores.slice(INITIAL_COUNT);
    if (rest.length) {
      const extra = document.createElement('div');
      extra.className = 'c-store-inventory__extra';
      const inner = document.createElement('div');
      inner.className = 'c-store-inventory__extra-inner';
      rest.forEach((store) => inner.appendChild(buildStoreItem(store)));
      extra.appendChild(inner);
      listEl.appendChild(extra);
    }
    updateMoreBtn();
  }

  function updateMoreBtn() {
    const extra = listEl.querySelector('.c-store-inventory__extra');
    moreBtn.hidden = !extra;
    if (extra) extra.classList.toggle('is-open', state.expanded);
    moreBtn.innerHTML = state.expanded
      ? '閉じる <span aria-hidden="true">−</span>'
      : 'もっと見る <span aria-hidden="true">＋</span>';
    moreBtn.setAttribute('aria-expanded', state.expanded ? 'true' : 'false');
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }
  function hideError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  /* 注意書きの [[時点]] プレースホルダーを在庫取得時点（JST）に置換。
     ※SQレスポンスに同期時刻フィールドがあると分かったらそちらに差し替える（実装計画参照） */
  const noticeTemplate = noticeEl ? noticeEl.innerHTML : '';
  function updateNoticeDate() {
    if (!noticeEl || noticeTemplate.indexOf('[[時点]]') === -1) return;
    const jst = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = jst.getFullYear() + '年' + pad(jst.getMonth() + 1) + '月' + pad(jst.getDate()) + '日 ' + jst.getHours() + '時';
    noticeEl.innerHTML = noticeTemplate.split('[[時点]]').join(stamp);
  }

  /* 外部URLのみ許可（DOM XSS 対策。Tabio Finding 7 と同じ） */
  function safeURL(url) {
    if (!url) return '';
    const clean = String(url).trim().toLowerCase();
    if (clean.indexOf('https://') === 0 || clean.indexOf('http://') === 0 || clean.indexOf('//') === 0) return url;
    return '';
  }

  /* ============ イベント ============ */

  selects.forEach((sel) => {
    sel.addEventListener('change', () => {
      const idx = parseInt(sel.dataset.optionIndex, 10);
      state.selectedOptions[idx] = sel.value;
      loadStores();
    });
  });

  moreBtn.addEventListener('click', () => {
    state.expanded = !state.expanded;
    updateMoreBtn();
    /* 閉じたときにリスト末尾までスクロールしていた場合に備え、リスト先頭へ戻す */
    if (!state.expanded) {
      listEl.closest('.c-store-inventory__stores').scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });

  triggerBtn.addEventListener('click', () => {
    dialog.showModal();
    document.documentElement.classList.add('c-store-inventory-open'); /* 背景スクロール固定 */
    loadStores();
  });

  root.querySelectorAll('[data-store-inventory-close]').forEach((btn) => {
    btn.addEventListener('click', () => dialog.close());
  });

  /* 背景（::backdrop 相当領域）クリックで閉じる */
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });

  /* close イベントで一括解除（×・背景クリック・Escすべてここを通る） */
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('c-store-inventory-open');
  });

  /* ============ ピクパ用デバッグモード（?sqdebug=1） ============
     showModal はトップレイヤーに乗るため PerfectPixel 等のオーバーレイが上に置けない。
     非モーダル（open属性）で出しっぱなし＋ダミー店舗を描画する。本番挙動には影響しない。 */
  if (new URLSearchParams(location.search).get('sqdebug')) {
    state.stores = [
      { name: 'サッポロファクトリー店', province: '北海道', address1: '札幌市', phone: '011-206-0382', mapURL: 'https://example.com', quantity: 10 },
      { name: '渋谷店', province: '東京都', address1: '渋谷区', phone: '03-3486-7820', mapURL: 'https://example.com', quantity: 10 },
      { name: '残りわずか店（△確認用）', province: '大阪府', address1: '大阪市', phone: '06-0000-0000', mapURL: '', quantity: 2 },
      { name: '在庫なし店（×確認用）', province: '福岡県', address1: '福岡市', phone: '092-000-0000', mapURL: '', quantity: 0 },
    ];
    dialog.classList.add('is-debug');
    dialog.setAttribute('open', '');
    updateNoticeDate();
    renderStores();
  }
});
